import { format } from 'date-fns';


export const formatDate = ( date ) => {
  if( !date ){
    return 'N/A';
  }
  try {
    const parsedDate = new Date( date );
    if( isNaN( parsedDate.getTime() ) ){
      return date || 'N/A';
    }
    return format( parsedDate, 'MM/dd/yyyy hh:mmaaa' );
  }
  catch ( e ){
    return date || 'N/A';
  }
};

const handleResponse = async( res, errorMsg ) => {
  if( !res.ok ){
    let details = '';
    const contentType = res.headers.get( 'content-type' );
    if( contentType && contentType.includes( 'application/json' ) ){
      try {
        const errorJson = await res.json();
        details = errorJson.message || errorJson.errors?.[0]?.message || '';
      }
      catch ( e ){
        // ignore JSON parsing errors
      }
    }
    else {
      try {
        details = await res.text();
      }
      catch ( e ){
        // ignore text parsing errors
      }
    }
    throw new Error( `${errorMsg}: ${res.status} ${res.statusText} ${details}`.trim() );
  }

  const contentType = res.headers.get( 'content-type' );
  if( contentType && contentType.includes( 'application/json' ) ){
    const json = await res.json();
    if( json.errors ){
      throw new Error( json.errors[0].message || 'GraphQL query failed' );
    }
    return json;
  }
  return res.text();
};

const safeFetch = async( url, errorMsg ) => {
  try {
    const res = await fetch( url );
    return await handleResponse( res, errorMsg );
  }
  catch ( err ){
    throw new Error( `${errorMsg} - ${err.message}` );
  }
};

const buildProxyUrl = ( domain, target, path ) => {
  return `${domain}/devpanel?action=amplience-proxy&target=${target}&path=${path}`;
};

export const fetchContentMetadata = async( contentId, utilityServerDomain ) => {
  if( !contentId ){
    return null;
  }
  const url = buildProxyUrl( utilityServerDomain, 'api', `/content/content-items/${contentId}` );
  return safeFetch( url, `Failed to fetch metadata for ID ${contentId}` );
};

export const fetchRepoDetails = async( repoId, utilityServerDomain ) => {
  if( !repoId ){
    return null;
  }
  const url = buildProxyUrl( utilityServerDomain, 'api', `/content/content-repositories/${repoId}` );
  const json = await safeFetch( url, `Failed to fetch repository details for ID ${repoId}` );
  return json.label || json.name || null;
};

export const fetchSlotSchedulingDetails = async( contentId, utilityServerDomain ) => {
  if( !contentId ){
    return null;
  }
  const url = buildProxyUrl( utilityServerDomain, 'api', `/slots/${contentId}/assignments` );
  return safeFetch( url, `Failed to fetch assignments for ID ${contentId}` );
};

export const fetchFolderDetails = async( folderId, utilityServerDomain ) => {
  if( !folderId ){
    return null;
  }
  const url = buildProxyUrl( utilityServerDomain, 'api', `/content/folders/${folderId}` );
  const json = await safeFetch( url, `Failed to fetch folder details for ID ${folderId}` );
  return json.name || json.label || null;
};

export const handleScrollToElement = ( module ) => {
  if( !module || !module.moduleName || !module.id ){
    return;
  }
  const { moduleName, id } = module;
  const elementID = `${moduleName}::${id}`;
  const selector = `[data-debug-id="${elementID}"]`;
  const element = document.querySelector( selector );

  if( element ){
    const target = element.nextElementSibling;
    if( !target ){
      // eslint-disable-next-line no-console
      console.warn( 'Could not find target element to scroll to', {
        selector,
        module
      } );
      return;
    }

    // Remove all existing highlights from the page to ensure only one is active
    const existingHighlights = document.querySelectorAll(
      '.animated-debug-border, .fade-debug-border'
    );
    existingHighlights.forEach( ( el ) => {
      if( el.highlightTimeout ){
        clearTimeout( el.highlightTimeout );
        // eslint-disable-next-line no-param-reassign
        el.highlightTimeout = null;
      }
      el.classList.remove( 'animated-debug-border', 'fade-debug-border' );
      // eslint-disable-next-line no-param-reassign
      el.style.animation = ''; // Reset any inline animation styles
    } );

    target.scrollIntoView( { behavior: 'smooth', block: 'center' } );

    // Apply animated border
    target.classList.add( 'animated-debug-border' );

    // After 3 seconds, remove the highlight
    target.highlightTimeout = setTimeout( () => {
      target.classList.remove( 'animated-debug-border' );
      target.classList.remove( 'fade-debug-border' );
      target.highlightTimeout = null;
    }, 3000 );
  }
  else {
    // eslint-disable-next-line no-console
    console.warn( 'Could not find element to scroll to', { selector, module } );
  }
};

export const fetchUserDetails = async( userId, utilityServerDomain ) => {
  if( !userId ){
    return null;
  }
  const url = buildProxyUrl( utilityServerDomain, 'auth', `/users/${userId}` );
  const json = await safeFetch( url, `Failed to fetch user details for ID ${userId}` );
  return json.data?.attributes || json.attributes || null;
};

export const getUserInfo = ( users, type ) => {
  const user = users?.[type];
  if( !user || user.error ){
    return 'Unknown';
  }
  const firstName = user['first-name'] || '';
  const lastName = user['last-name'] || '';
  const statusIcon = user.status === 'ACTIVE' ? '✅' : '❌';

  if( !firstName && !lastName ){
    return 'Unknown User';
  }

  return `${lastName}, ${firstName} ${statusIcon}`.trim();
};
